import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { loadJobastiLogoDataUrl } from './invoicePdf';

const formatMoney = (cents, currency = 'EUR') => {
  if (!cents && cents !== 0) return '—';
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format((cents || 0) / 100); }
  catch { return `${Math.round((cents || 0) / 100)} ${currency}`; }
};

const granularityLabel = (g) => g === 'quarter' ? 'Quarterly' : g === 'year' ? 'Yearly' : 'Monthly';

/**
 * Capture a chart container (a DOM element) as a PNG dataURL.
 * Returns null on failure (caller should skip embedding).
 */
async function _captureToPng(element) {
  if (!element) return null;
  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
    });
    return canvas.toDataURL('image/png');
  } catch (e) {
    console.warn('Chart capture failed:', e);
    return null;
  }
}

/**
 * Build the Super Admin analytics PDF — exec summary, KPIs, charts, top revenue.
 *
 * @param {object} params
 * @param {object} params.overview        - response from /api/analytics/overview
 * @param {object} params.dashboard       - response from /api/analytics/dashboard
 * @param {string} params.granularity     - 'month' | 'quarter' | 'year'
 * @param {string[]} [params.chartElementIds] - DOM ids of chart containers to embed
 */
export async function downloadAdminAnalyticsPdf({
  overview,
  dashboard,
  granularity,
  chartElementIds = [],
}) {
  if (!overview || !dashboard) return;
  const currency = overview.currency || 'EUR';
  const logo = await loadJobastiLogoDataUrl();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  const today = new Date();

  // ── Header band ──────────────────────────────────────────────────────────
  doc.setFillColor(245, 248, 252).rect(0, 0, pageW, 100, 'F');
  if (logo) { try { doc.addImage(logo, 'PNG', margin, 30, 110, 36, undefined, 'FAST'); } catch { /* ignore */ } }
  else { doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(15, 23, 42).text('JobastiIQ', margin, 56); }
  doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(15, 23, 42)
    .text('Platform Analytics', pageW - margin, 50, { align: 'right' });
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(100, 116, 139)
    .text(`Granularity: ${granularityLabel(granularity)}  ·  ${overview.buckets[0]} → ${overview.buckets[overview.buckets.length - 1]}`, pageW - margin, 68, { align: 'right' })
    .text(`Generated ${today.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, pageW - margin, 82, { align: 'right' });

  // ── This-month KPI band ──────────────────────────────────────────────────
  let y = 130;
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(15, 23, 42).text('This month', margin, y);
  y += 14;
  const monthCards = [
    { label: 'Licenses sold', value: String(dashboard.licensesSoldThisMonth) },
    { label: 'Turnover', value: formatMoney(dashboard.turnoverCents, currency) },
    { label: 'Sales made', value: String(dashboard.licenseSalesThisMonth) },
    { label: 'New companies', value: String(dashboard.newCompaniesThisMonth) },
  ];
  _drawKpiRow(doc, monthCards, margin, y, pageW - margin * 2);
  y += 70;

  // ── Period totals KPI band ──────────────────────────────────────────────
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(15, 23, 42)
    .text(`Last ${overview.periods} ${granularityLabel(granularity).toLowerCase().replace(/ly$/, '')}${overview.periods > 1 ? 's' : ''}`, margin, y);
  y += 14;
  const totalCards = [
    { label: 'Licenses', value: String(overview.totals.licenses) },
    { label: 'Turnover', value: formatMoney(overview.totals.turnoverCents, currency) },
    { label: 'Sales made', value: String(overview.totals.licenseSales) },
    { label: 'Companies', value: String(overview.totals.newCompanies) },
    { label: 'Applications', value: String(overview.totals.applications) },
    { label: 'Prospects', value: String(overview.totals.prospects) },
  ];
  _drawKpiRow(doc, totalCards, margin, y, pageW - margin * 2, 6);
  y += 70;

  // ── Breakdown table ──────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(15, 23, 42).text('Period breakdown', margin, y);
  const rows = overview.series.map((s) => [
    s.bucket,
    String(s.licenses),
    formatMoney(s.turnoverCents, currency),
    String(s.licenseSales),
    String(s.newCompanies),
    String(s.applications),
    String(s.prospects),
  ]);
  autoTable(doc, {
    startY: y + 8,
    head: [['Period', 'Licenses', 'Turnover', 'Sales made', 'Companies', 'Apps', 'Prospects']],
    body: rows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 6, textColor: [30, 41, 59], lineColor: [226, 232, 240] },
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
  });

  // ── Top revenue companies ────────────────────────────────────────────────
  if (overview.topRevenue && overview.topRevenue.length > 0) {
    const startY = doc.lastAutoTable.finalY + 24;
    doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(15, 23, 42).text('Top revenue companies', margin, startY);
    autoTable(doc, {
      startY: startY + 8,
      head: [['Company', 'Revenue']],
      body: overview.topRevenue.map((r) => [r.name, formatMoney(r.cents, currency)]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 9.5, cellPadding: 7, textColor: [30, 41, 59], lineColor: [226, 232, 240] },
      headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
    });
  }

  // ── Charts page(s) ───────────────────────────────────────────────────────
  if (chartElementIds.length > 0) {
    for (const id of chartElementIds) {
      const el = document.getElementById(id);
      const png = await _captureToPng(el);
      if (!png) continue;
      doc.addPage();
      const title = el?.getAttribute('data-pdf-title') || 'Chart';
      doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor(15, 23, 42).text(title, margin, 60);
      const maxW = pageW - margin * 2;
      const maxH = pageH - 120;
      // Maintain aspect: use image natural ratio from canvas (assume captured at 2x)
      const imgProps = doc.getImageProperties(png);
      const ratio = imgProps.width / imgProps.height;
      let w = maxW, h = w / ratio;
      if (h > maxH) { h = maxH; w = h * ratio; }
      doc.addImage(png, 'PNG', margin, 80, w, h, undefined, 'FAST');
    }
  }

  // ── Footer (last page) ───────────────────────────────────────────────────
  const footerY = doc.internal.pageSize.getHeight() - 28;
  doc.setDrawColor(226, 232, 240).setLineWidth(0.5);
  doc.line(margin, footerY - 10, pageW - margin, footerY - 10);
  doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(148, 163, 184)
    .text('JobastiIQ GmbH  ·  Generated from /admin/analytics', pageW / 2, footerY, { align: 'center' });

  const filename = `JobastiIQ-Analytics-${granularity}-${today.toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

/**
 * Build the recruiter company analytics PDF — Workable-style layout with
 * headline KPIs, rate metrics, funnel, application stages and embedded charts.
 *
 * Accepts the response shape returned by
 *   GET /api/analytics/company/:orgId/recruiting
 */
export async function downloadCompanyAnalyticsPdf({
  data,
  companyName,
  rangeLabel,
  chartElementIds = [],
}) {
  if (!data) return;
  const logo = await loadJobastiLogoDataUrl();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  const today = new Date();

  const dateRangeStr = (() => {
    try {
      const s = new Date(data.rangeStart).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      const e = new Date(data.rangeEnd).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      return `${s} → ${e}`;
    } catch { return rangeLabel || ''; }
  })();

  // Header
  doc.setFillColor(245, 248, 252).rect(0, 0, pageW, 100, 'F');
  if (logo) { try { doc.addImage(logo, 'PNG', margin, 30, 110, 36, undefined, 'FAST'); } catch { /* ignore */ } }
  else { doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(15, 23, 42).text('JobastiIQ', margin, 56); }
  doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(15, 23, 42)
    .text('Recruiting Analytics', pageW - margin, 50, { align: 'right' });
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(100, 116, 139)
    .text(`${companyName || ''}  ·  ${rangeLabel || ''}  ·  ${dateRangeStr}`, pageW - margin, 68, { align: 'right' })
    .text(`Generated ${today.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, pageW - margin, 82, { align: 'right' });

  // Row 1 KPIs — 6 tiles
  let y = 130;
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(15, 23, 42).text('Headline metrics', margin, y);
  y += 14;
  const kpiCards = [
    { label: 'Applications', value: String(data.kpis.applications.value) },
    { label: 'High match (≥80%)', value: String(data.kpis.highMatch.value) },
    { label: 'Interviews', value: String(data.kpis.interviews.value) },
    { label: 'Offers', value: String(data.kpis.offers.value) },
    { label: 'Hires', value: String(data.kpis.hires.value) },
    { label: 'Active positions', value: String(data.kpis.activePositions.value) },
  ];
  _drawKpiRow(doc, kpiCards, margin, y, pageW - margin * 2, 6);
  y += 70;

  // Row 2 rate metrics — 6 tiles
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(15, 23, 42).text('Rate & timing metrics', margin, y);
  y += 14;
  const rate = (v, suffix = '') => v === null || v === undefined ? '—' : `${v}${suffix}`;
  const rateCards = [
    { label: 'Avg. match score', value: rate(data.rates.avgMatchScore.value, '%') },
    { label: 'Time to first interview', value: rate(data.rates.avgTimeToFirstInterview.value, ' d') },
    { label: 'Time to offer', value: rate(data.rates.avgTimeToOffer.value, ' d') },
    { label: 'Time to hire', value: rate(data.rates.avgTimeToHire.value, ' d') },
    { label: 'Show-up rate', value: rate(data.rates.interviewShowUpRate.value, '%') },
    { label: 'Offer acceptance', value: rate(data.rates.offerAcceptanceRate.value, '%') },
  ];
  _drawKpiRow(doc, rateCards, margin, y, pageW - margin * 2, 6);
  y += 70;

  // Recruiting funnel
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(15, 23, 42).text('Recruiting funnel', margin, y);
  autoTable(doc, {
    startY: y + 8,
    head: [['Stage', 'Count', '% of total']],
    body: data.stages.map((s) => [
      s.label,
      String(s.value),
      `${s.pctTotal}%`,
    ]),
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 6, textColor: [30, 41, 59], lineColor: [226, 232, 240] },
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
  });

  // Position performance table
  if (data.positionPerformance?.length > 0) {
    const startY = doc.lastAutoTable.finalY + 24;
    doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(15, 23, 42).text('Hiring performance by position', margin, startY);
    autoTable(doc, {
      startY: startY + 8,
      head: [['Position', 'Applications', 'Interviews', 'Hires']],
      body: data.positionPerformance.map((r) => [r.title, String(r.applications), String(r.interviews), String(r.hires)]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 6, textColor: [30, 41, 59], lineColor: [226, 232, 240] },
      headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    });
  }

  // Needs attention
  if (data.needsAttention?.length > 0) {
    const startY = doc.lastAutoTable.finalY + 24;
    doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(180, 83, 9).text('Needs attention', margin, startY);
    autoTable(doc, {
      startY: startY + 8,
      head: [['Position', 'Reason']],
      body: data.needsAttention.map((n) => [n.title, n.reason]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 6, textColor: [30, 41, 59], lineColor: [253, 230, 138] },
      headStyles: { fillColor: [251, 191, 36], textColor: [69, 26, 3], fontStyle: 'bold', fontSize: 9 },
    });
  }

  // Charts pages
  if (chartElementIds.length > 0) {
    for (const id of chartElementIds) {
      const el = document.getElementById(id);
      const png = await _captureToPng(el);
      if (!png) continue;
      doc.addPage();
      const title = el?.getAttribute('data-pdf-title') || 'Chart';
      doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor(15, 23, 42).text(title, margin, 60);
      const maxW = pageW - margin * 2;
      const maxH = pageH - 120;
      const imgProps = doc.getImageProperties(png);
      const ratio = imgProps.width / imgProps.height;
      let w = maxW, h = w / ratio;
      if (h > maxH) { h = maxH; w = h * ratio; }
      doc.addImage(png, 'PNG', margin, 80, w, h, undefined, 'FAST');
    }
  }

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 28;
  doc.setDrawColor(226, 232, 240).setLineWidth(0.5);
  doc.line(margin, footerY - 10, pageW - margin, footerY - 10);
  doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(148, 163, 184)
    .text(`JobastiIQ  ·  ${companyName || 'Company'}  ·  Generated from /my-company → Analytics  ·  Finance is intentionally excluded`, pageW / 2, footerY, { align: 'center' });

  const safeName = (companyName || 'Company').replace(/[^a-z0-9-]/gi, '_');
  const safeRange = (data.range || 'range').replace(/[^a-z0-9-]/gi, '_');
  doc.save(`${safeName}-Analytics-${safeRange}-${today.toISOString().slice(0, 10)}.pdf`);
}

/** Internal: draw a row of KPI cards spanning the given width. */
function _drawKpiRow(doc, cards, x, y, totalWidth, cols = 4) {
  const gap = 6;
  const w = (totalWidth - gap * (cols - 1)) / cols;
  const h = 56;
  cards.forEach((c, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = x + col * (w + gap);
    const cy = y + row * (h + gap);
    doc.setDrawColor(226, 232, 240).setFillColor(255, 255, 255);
    doc.roundedRect(cx, cy, w, h, 6, 6, 'FD');
    doc.setFont('helvetica', 'bold').setFontSize(16).setTextColor(15, 23, 42);
    doc.text(String(c.value), cx + 10, cy + 28);
    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(100, 116, 139);
    doc.text(String(c.label).toUpperCase(), cx + 10, cy + 44);
  });
}
